import React, { useState } from "react";
import {
  Package,
  Calendar,
  Clock,
  User,
  MapPin,
  Search,
  Filter,
  CheckCircle,
  Truck,
  Users,
  AlertCircle,
  RefreshCw,
  FileText,
  Printer,
  Download,
  X,
  Map,
  Navigation,
  List,
} from "lucide-react";
import { useOrders } from "../hooks/useOrders";
import { useAgents } from "../hooks/useAgents";
import OrdersMapView from "./OrdersMapView";
import "../styles/Home.css";

const Orders = () => {
  // Use real API integration
  const {
    orders: apiOrders,
    loading,
    error,
    updateOrderStatus: apiUpdateOrderStatus,
    assignOrderToAgent,
    updateDeliveryStatus,
    refreshOrders,
    clearError,
    isAuthenticated,
  } = useOrders();

  const [statusFilter, setStatusFilter] = useState("pending");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [deliveryDate, setDeliveryDate] = useState("");
  const [deliveryTimeSlot, setDeliveryTimeSlot] = useState("");
  const [deliveryNotes, setDeliveryNotes] = useState("");
  const [assignedAgent, setAssignedAgent] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedOrders, setExpandedOrders] = useState(new Set());
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceOrder, setInvoiceOrder] = useState(null);
  const [updatingOrder, setUpdatingOrder] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'map'

  // Function to open Google Maps with coordinates
  const openGoogleMaps = (latitude, longitude, address = "") => {
    if (latitude && longitude) {
      // Google Maps URL with coordinates
      const url = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
      window.open(url, '_blank');
    } else if (address) {
      // Fallback to address search if no coordinates
      const encodedAddress = encodeURIComponent(address);
      const url = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
      window.open(url, '_blank');
    } else {
      alert("Location information not available for this order");
    }
  };

  // Function to format phone numbers with +91 prefix
  const formatPhoneNumber = (phone) => {
    if (!phone || phone === "N/A") return "N/A";
    
    // Remove all non-digit characters
    const cleaned = phone.toString().replace(/\D/g, '');
    
    // If it starts with 91, remove it as we'll add +91
    const number = cleaned.startsWith('91') ? cleaned.slice(2) : cleaned;
    
    // Format as +91 XXXXX XXXXX
    if (number.length === 10) {
      return `+91 ${number.slice(0, 5)} ${number.slice(5)}`;
    }
    
    // Return with +91 prefix even if not 10 digits
    return `+91 ${number}`;
  };

  // Transform API orders to match component format
  const orders = apiOrders.map((order) => {
    const orderId = order.orderId || order._id;

    // Debug logging to see what we're getting
    console.log("Order data:", {
      orderId,
      backendStatus: order.status,
    });

    return {
      id: order.orderId || order._id,
      // Customer info comes from userId object (the user who placed the order)
      customerName:
        order.userId?.fullName || order.userId?.userName || "Unknown Customer",
      customerPhone: formatPhoneNumber(order.userId?.phoneNumber),
      customerEmail: order.userId?.email || "N/A",
      customerAddress: order.deliveryAddress?.street || order.deliveryAddress || "No address provided",
      deliveryLocation: {
        latitude: order.deliveryAddress?.latitude || null,
        longitude: order.deliveryAddress?.longitude || null,
        city: order.deliveryAddress?.city || "",
        state: order.deliveryAddress?.state || "",
        pincode: order.deliveryAddress?.pincode || "",
        landmark: order.deliveryAddress?.landmark || "",
        fullAddress: order.deliveryAddress?.street || order.deliveryAddress || "",
      },
      orderDate: order.orderDate
        ? new Date(order.orderDate).toLocaleDateString()
        : "N/A",
      orderTime: order.orderDate
        ? new Date(order.orderDate).toLocaleTimeString()
        : "N/A",
      status:
        order.status === "processing"
          ? "pending"
          : order.status === "accepted"
          ? "accepted"
          : order.status === "ready"
          ? "ready"
          : order.status === "assigned"
          ? "assigned"
          : order.status === "picked_up"
          ? "picked_up"
          : order.status === "in_transit"
          ? "in_transit"
          : order.status === "delivered"
          ? "delivered"
          : order.status === "failed"
          ? "failed"
          : order.status === "cancel"
          ? "cancelled"
          : order.status,
      deliveryDate: order.deliveryDate || null,
      totalAmount: order.totalAmount || 0,
      subTotal: order.subTotal || 0,
      shippingCharge: order.shippingCharge || 0,
      items:
        order.products?.map((productItem) => ({
          id: productItem.product?._id || productItem._id || "unknown",
          name: productItem.product?.productName || "Unknown Product",
          brandName: productItem.product?.brandName || "N/A",
          category: productItem.product?.category || "N/A",
          quantity: productItem.quantity || 0,
          price:
            productItem.product?.discountPrice ||
            productItem.product?.orignalPrice ||
            0,
          originalPrice: productItem.product?.orignalPrice || 0,
          discountPrice: productItem.product?.discountPrice || 0,
          image: productItem.product?.images?.[0]?.Location || null,
        })) || [],
      paymentMethod: order.paymentMethod || "N/A",
      notes: order.notes || "",
      assignedAgent: order.deliveryAgentId
        ? {
            id: order.deliveryAgentId._id || order.deliveryAgentId,
            name: order.deliveryAgentId.fullName || "Unknown Agent",
            bikeNumber: order.deliveryAgentId.vehicleNumber || "N/A",
            phone: formatPhoneNumber(order.deliveryAgentId.phoneNumber),
          }
        : null,
      expectedDeliveryDate: order.expectedDeliveryDate || null,
      deliveryTimeSlot: order.deliveryTimeSlot || null,
      assignedAt: order.assignedAt || null,
      vendorId: order.vendorId || null,
      // Store the complete userId object for reference (contains customer details)
      userId: order.userId || null,
    };
  });

  // Function to toggle order items expansion
  const toggleOrderExpansion = (orderId) => {
    setExpandedOrders((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  // Function to open invoice modal
  const openInvoice = (order) => {
    setInvoiceOrder(order);
    setShowInvoiceModal(true);
  };

  // Function to print invoice
  const printInvoice = () => {
    // Create a new window for printing
    const printWindow = window.open("", "_blank");
    const invoiceContent = document.getElementById("invoice-content").innerHTML;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Invoice - ${generateInvoiceNumber(invoiceOrder)}</title>
          <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css" rel="stylesheet">
          <style>
            @media print {
              body { 
                margin: 0; 
                padding: 15px;
                font-family: Arial, sans-serif;
                font-size: 12px;
                line-height: 1.3;
              }
              h3, h4 { font-size: 16px !important; margin: 0 !important; }
              .small { font-size: 11px !important; }
              .table { 
                border-collapse: collapse !important;
                margin: 0 !important;
                width: 100% !important;
              }
              .table th { 
                border: 1px solid #000 !important; 
                padding: 6px 8px !important;
                font-size: 11px !important;
                background-color: #333 !important;
                color: white !important;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              .table td { 
                border: 1px solid #000 !important; 
                padding: 5px 8px !important;
                font-size: 11px !important;
              }
              .table tbody tr:nth-child(even) {
                background-color: #f8f9fa !important;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              .table tfoot tr {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              .table tfoot tr:last-child {
                background-color: #333 !important;
                color: white !important;
                font-weight: bold !important;
              }
              .border { border-color: #000 !important; }
              .mb-2, .mb-3, .mt-2, .mt-3 { margin: 8px 0 !important; }
              .p-2, .p-3 { padding: 6px !important; }
            }
            body { 
              font-family: Arial, sans-serif; 
              line-height: 1.4;
            }
          </style>
        </head>
        <body>
          <div class="invoice-header">
            ${invoiceContent}
          </div>
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() {
                window.close();
              }
            }
          </script>
        </body>
      </html>
    `);

    printWindow.document.close();
  };

  // Alternative simple print function
  const simplePrint = () => {
    window.print();
  };

  // Function to download as PDF (using browser's print to PDF)
  const downloadPDF = () => {
    // This will open the browser's print dialog where user can choose "Save as PDF"
    const printWindow = window.open("", "_blank");
    const invoiceContent = document.getElementById("invoice-content").innerHTML;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Invoice - ${generateInvoiceNumber(invoiceOrder)}</title>
          <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css" rel="stylesheet">
          <style>
            body { 
              margin: 0; 
              padding: 15px;
              font-family: Arial, sans-serif;
              font-size: 12px;
              line-height: 1.3;
            }
            h3, h4 { font-size: 16px; margin: 0; }
            .small { font-size: 11px; }
            .table { 
              border-collapse: collapse !important;
              width: 100%;
              margin: 0;
            }
            .table th { 
              border: 1px solid #000 !important; 
              padding: 6px 8px !important;
              font-size: 11px;
              background-color: #333 !important;
              color: white !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            .table td { 
              border: 1px solid #000 !important; 
              padding: 5px 8px !important;
              font-size: 11px;
            }
            .table tbody tr:nth-child(even) {
              background-color: #f8f9fa !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            .table tfoot tr {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            .table tfoot tr:last-child {
              background-color: #333 !important;
              color: white !important;
              font-weight: bold !important;
            }
            .border { border-color: #000; }
            .mb-2, .mb-3, .mt-2, .mt-3 { margin: 8px 0; }
            .p-2, .p-3 { padding: 6px; }
          </style>
        </head>
        <body>
          ${invoiceContent}
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
              }, 500);
            }
          </script>
        </body>
      </html>
    `);

    printWindow.document.close();
  };

  // Function to generate invoice number
  const generateInvoiceNumber = (order) => {
    // Use the raw order date from API, not the formatted one
    const rawOrder = apiOrders.find(o => (o.orderId || o._id) === order.id);
    const orderDateStr = rawOrder?.orderDate || Date.now();
    const date = new Date(orderDateStr);
    
    // Validate date
    if (isNaN(date.getTime())) {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      return `INV-${year}${month}-${order.id.slice(-6)}`;
    }
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    return `INV-${year}${month}-${order.id.slice(-6)}`;
  };

  // Real agents data from API
  const { agents: apiAgents } = useAgents();

  const agents = (apiAgents || []).map((agent) => ({
    id: agent._id,
    name: agent.fullName,
    phone: formatPhoneNumber(agent.phoneNumber),
    bikeNumber: agent.vehicleNumber || agent.bikeNumber || "N/A",
    status: agent.isActive ? agent.status || "active" : "inactive",
  }));

  const statusOptions = [
    { value: "all", label: "All Orders", color: "secondary" },
    { value: "pending", label: "Pending", color: "warning" },
    { value: "accepted", label: "Accepted", color: "info" },
    { value: "rejected", label: "Rejected", color: "danger" },
    { value: "ready", label: "Ready", color: "info" },
    { value: "assigned", label: "Assigned", color: "primary" },
    { value: "delivered", label: "Delivered", color: "success" },
  ];

  const filteredOrders = orders.filter((order) => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch =
      searchTerm === "" ||
      order.id.toLowerCase().includes(searchLower) ||
      order.customerName.toLowerCase().includes(searchLower) ||
      order.customerPhone.toString().includes(searchTerm) ||
      order.customerEmail.toLowerCase().includes(searchLower) ||
      order.customerAddress.toLowerCase().includes(searchLower) ||
      order.items.some(
        (item) =>
          item.name.toLowerCase().includes(searchLower) ||
          item.brandName.toLowerCase().includes(searchLower) ||
          item.category.toLowerCase().includes(searchLower)
      );
    const matchesStatus =
      statusFilter === "all" || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleStatusUpdate = async (orderId, newStatus) => {
    // For assignment, we need special handling
    if (newStatus === "assign") {
      const order = orders.find((o) => o.id === orderId);

      // Check if order is in correct status for assignment (accepted or ready)
      if (order && order.status !== "ready" && order.status !== "accepted") {
        alert("Order must be accepted before assigning an agent.");
        return;
      }

      // Order is accepted/ready, show assignment modal directly
      setSelectedOrder(orderId);
      setShowDeliveryModal(true);
      return;
    }

    // Handle delivered status with delivery agents API
    if (newStatus === "delivered") {
      try {
        // Find the order to get the delivery agent ID
        const order = orders.find((o) => o.id === orderId);
        if (!order || !order.assignedAgent) {
          alert(
            "No delivery agent assigned to this order. Please assign an agent first."
          );
          return;
        }

        setUpdatingOrder(orderId);

        // Use the hook function which handles API call and local state update
        await updateDeliveryStatus(
          orderId,
          "delivered",
          order.assignedAgent.id
        );

        // Refresh orders to get latest data from server
        await refreshOrders();

        alert("Order marked as delivered successfully!");
      } catch (error) {
        console.error("Failed to mark order as delivered:", error);
        alert("Failed to mark order as delivered. Please try again.");
      } finally {
        setUpdatingOrder(null);
      }
      return;
    }

    try {
      setUpdatingOrder(orderId);

      // Map component status to API status
      let apiStatus = newStatus;
      let successMessage = "Order status updated successfully!";

      if (newStatus === "pending") {
        apiStatus = "processing";
        successMessage = "Order marked as pending!";
      } else if (newStatus === "accepted") {
        apiStatus = "accepted";
        successMessage = "Order accepted successfully!";
      } else if (newStatus === "rejected") {
        apiStatus = "rejected";
        successMessage = "Order rejected successfully!";
      } else if (newStatus === "cancelled") {
        apiStatus = "cancel";
        successMessage = "Order cancelled successfully!";
      }

      await apiUpdateOrderStatus(orderId, apiStatus);

      // Show success message to user
      alert(successMessage);
    } catch (error) {
      console.error("Failed to update order status:", error);
      alert("Failed to update order status. Please try again.");
    } finally {
      setUpdatingOrder(null);
    }
  };

  const handleDeliveryDateConfirm = async () => {
    if (!deliveryDate) {
      alert("Please select a delivery date");
      return;
    }

    if (!assignedAgent) {
      alert("Please select a delivery agent");
      return;
    }

    if (!deliveryTimeSlot) {
      alert("Please select a delivery time slot");
      return;
    }

    try {
      // Convert date to ISO string for API
      const deliveryDateTime = new Date(deliveryDate).toISOString();

      // Assign order to agent with delivery date and time slot
      await assignOrderToAgent(
        selectedOrder,
        assignedAgent,
        deliveryDateTime,
        deliveryTimeSlot,
        deliveryNotes
      );

      // Refresh orders to get latest data from server
      await refreshOrders();

      alert("Order assigned to delivery agent successfully!");

      setShowDeliveryModal(false);
      setSelectedOrder(null);
      setDeliveryDate("");
      setDeliveryTimeSlot("");
      setDeliveryNotes("");
      setAssignedAgent("");
    } catch (error) {
      console.error("Failed to assign order:", error);
      alert("Failed to assign order to agent. Please try again.");
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: "warning", icon: Clock, text: "Pending" },
      processing: { color: "warning", icon: Clock, text: "Processing" },
      ready: { color: "info", icon: Package, text: "Ready" },
      accepted: { color: "info", icon: Package, text: "Accepted" },
      assigned: { color: "primary", icon: Truck, text: "Assigned" },
      picked_up: { color: "info", icon: Users, text: "Picked Up" },
      in_transit: { color: "primary", icon: Truck, text: "In Transit" },
      delivered: { color: "success", icon: CheckCircle, text: "Delivered" },
      failed: { color: "danger", icon: AlertCircle, text: "Failed" },
      cancelled: { color: "danger", icon: AlertCircle, text: "Cancelled" },
      rejected: { color: "danger", icon: AlertCircle, text: "Rejected" },
      cancel: { color: "danger", icon: AlertCircle, text: "Cancelled" },
    };

    const config = statusConfig[status] || statusConfig.pending;
    const IconComponent = config.icon;

    return (
      <span
        className={`badge bg-${config.color} d-flex align-items-center gap-1`}
      >
        <IconComponent size={8} />
        {config.text}
      </span>
    );
  };

  const getStatusActions = (order) => {
    const isUpdating = updatingOrder === order.id;

    switch (order.status) {
      case "pending":
      case "processing":
        return (
          <div className="d-flex flex-wrap gap-1">
            <button
              className="btn btn-success btn-sm px-2 py-1"
              onClick={() => handleStatusUpdate(order.id, "accepted")}
              disabled={isUpdating}
              style={{ fontSize: "0.75rem" }}
            >
              {isUpdating ? (
                <>
                  <div
                    className="spinner-border spinner-border-sm me-1"
                    role="status"
                    style={{ width: "12px", height: "12px" }}
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  Accepting...
                </>
              ) : (
                <>
                  <CheckCircle size={12} className="me-1" />
                  Accept
                </>
              )}
            </button>
            <button
              className="btn btn-danger btn-sm px-2 py-1"
              onClick={() => handleStatusUpdate(order.id, "rejected")}
              disabled={isUpdating}
              style={{ fontSize: "0.75rem" }}
            >
              {isUpdating ? (
                <>
                  <div
                    className="spinner-border spinner-border-sm me-1"
                    role="status"
                    style={{ width: "12px", height: "12px" }}
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  Rejecting...
                </>
              ) : (
                <>
                  <AlertCircle size={12} className="me-1" />
                  Reject
                </>
              )}
            </button>
          </div>
        );
      case "accepted":
      case "ready":
        // Show assign agent button for accepted orders
        if (!order.assignedAgent) {
          return (
            <button
              className="btn btn-warning btn-sm px-2 py-1"
              onClick={() => handleStatusUpdate(order.id, "assign")}
              style={{ fontSize: "0.75rem" }}
            >
              <Users size={12} className="me-1" />
              Assign Agent
            </button>
          );
        } else {
          return (
            <button
              className="btn btn-primary btn-sm px-2 py-1"
              onClick={() => handleStatusUpdate(order.id, "delivered")}
              disabled={isUpdating}
              style={{ fontSize: "0.75rem" }}
            >
              {isUpdating ? (
                <>
                  <div
                    className="spinner-border spinner-border-sm me-1"
                    role="status"
                    style={{ width: "12px", height: "12px" }}
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  Delivering...
                </>
              ) : (
                <>
                  <Truck size={12} className="me-1" />
                  Mark Delivered
                </>
              )}
            </button>
          );
        }
      case "assigned":
      case "picked_up":
      case "in_transit":
        return (
          <button
            className="btn btn-primary btn-sm px-2 py-1"
            onClick={() => handleStatusUpdate(order.id, "delivered")}
            disabled={isUpdating}
            style={{ fontSize: "0.75rem" }}
          >
            {isUpdating ? (
              <>
                <div
                  className="spinner-border spinner-border-sm me-1"
                  role="status"
                  style={{ width: "12px", height: "12px" }}
                >
                  <span className="visually-hidden">Loading...</span>
                </div>
                Delivering...
              </>
            ) : (
              <>
                <Truck size={12} className="me-1" />
                Mark Delivered
              </>
            )}
          </button>
        );
      case "delivered":
        return (
          <span className="text-success small" style={{ fontSize: "0.75rem" }}>
            <CheckCircle size={12} className="me-1" />
            Completed
          </span>
        );
      case "rejected":
        return (
          <span className="text-danger small" style={{ fontSize: "0.75rem" }}>
            <AlertCircle size={12} className="me-1" />
            Rejected
          </span>
        );
      case "cancelled":
      case "cancel":
        return (
          <span
            className="text-secondary small"
            style={{ fontSize: "0.75rem" }}
          >
            <Clock size={12} className="me-1" />
            Cancelled
          </span>
        );
      case "failed":
        return (
          <button
            className="btn btn-warning btn-sm px-2 py-1"
            onClick={() => handleStatusUpdate(order.id, "assign")}
            style={{ fontSize: "0.75rem" }}
          >
            <RefreshCw size={12} className="me-1" />
            Retry Delivery
          </button>
        );
      default:
        return null;
    }
  };

  // Calculate summary statistics for header stat cards - use all orders, not filtered
  const pendingOrdersCount = orders.filter(
    (order) => order.status === "pending" || order.status === "processing"
  ).length;

  const readyOrdersCount = orders.filter(
    (order) => order.status === "ready" || order.status === "accepted"
  ).length;

  const assignedOrdersCount = orders.filter(
    (order) => order.status === "assigned"
  ).length;

  const deliveredOrdersCount = orders.filter(
    (order) => order.status === "delivered"
  ).length;

  const rejectedOrdersCount = orders.filter(
    (order) =>
      order.status === "rejected" ||
      order.status === "cancelled" ||
      order.status === "failed"
  ).length;

  return (
    <div className="orders-page">
      {/* Enhanced Header */}
      <div className="orders-header">
        <div className="header-content">
          <div className="header-title-section">
            <h1 className="page-title">
              <Package size={24} />
              Orders Management
            </h1>
            <p className="page-subtitle">
              Track and manage all customer orders
            </p>
          </div>

          <div className="header-stats">
            <div className="stat-card stat-total">
              <div className="stat-value">{orders.length}</div>
              <div className="stat-label">Total Orders</div>
            </div>
            <div className="stat-card stat-pending">
              <div className="stat-value">{pendingOrdersCount}</div>
              <div className="stat-label">Pending</div>
            </div>
            <div className="stat-card stat-ready">
              <div className="stat-value">{readyOrdersCount}</div>
              <div className="stat-label">Ready</div>
            </div>
            <div className="stat-card stat-assigned">
              <div className="stat-value">{assignedOrdersCount}</div>
              <div className="stat-label">Assigned</div>
            </div>
            <div className="stat-card stat-delivered">
              <div className="stat-value">{deliveredOrdersCount}</div>
              <div className="stat-label">Delivered</div>
            </div>
            <div className="stat-card stat-rejected">
              <div className="stat-value">{rejectedOrdersCount}</div>
              <div className="stat-label">Rejected</div>
            </div>
          </div>
        </div>
      </div>

      <div className="orders-content">
        {/* Authentication Check */}
        {!isAuthenticated && (
          <div className="alert-card alert-warning">
            <AlertCircle size={20} />
            <div>
              <strong>Authentication Required</strong>
              <p>Please sign in to view and manage orders</p>
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="alert-card alert-error">
            <AlertCircle size={20} />
            <div>
              <strong>Error</strong>
              <p>{error}</p>
            </div>
            <button className="btn-dismiss" onClick={clearError}>
              <X size={16} />
            </button>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="loading-section">
            <div className="loading-spinner">
              <RefreshCw size={24} className="spinning" />
            </div>
            <p className="loading-text">Loading orders...</p>
          </div>
        )}

        {/* Refresh Button */}
        <div className="d-flex justify-content-end mx-3 mb-3">
          <button
            className="btn btn-outline-primary"
            onClick={refreshOrders}
            disabled={loading}
          >
            <RefreshCw
              size={16}
              className={`me-2 ${loading ? "spinning" : ""}`}
            />
            Refresh Orders
          </button>
        </div>

        {/* Search and Filter */}
        <div className="row mb-4 mx-3">
          <div className="col-md-8">
            <div className="row">
              <div className="col-md-6">
                <div className="input-group">
                  <span className="input-group-text">
                    <Search size={16} />
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by order ID, customer name, phone, email, product name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="input-group">
                  <span className="input-group-text">
                    <Filter size={16} />
                  </span>
                  <select
                    className="form-select"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    {statusOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Orders Cards */}
        <div className="mx-3">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h5 className="mb-0">{viewMode === 'list' ? 'Orders List' : 'Orders Map View'}</h5>
            <div className="d-flex align-items-center gap-3">
              <span className="text-muted">
                {filteredOrders.length} orders found
              </span>
              <div className="btn-group" role="group">
                <button
                  type="button"
                  className={`btn btn-sm ${viewMode === 'list' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setViewMode('list')}
                  title="List View"
                >
                  <List size={16} className="me-1" />
                  List
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${viewMode === 'map' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setViewMode('map')}
                  title="Map View"
                >
                  <Map size={16} className="me-1" />
                  Map
                </button>
              </div>
            </div>
          </div>

          {/* Map View */}
          {viewMode === 'map' ? (
            <OrdersMapView 
              orders={filteredOrders} 
              onOrderClick={(order) => {
                setSelectedOrder(order);
                
                // Trigger modal to open - using Bootstrap 5
                setTimeout(() => {
                  const modalElement = document.getElementById(`orderModal${order.id}`);
                  
                  if (modalElement && window.bootstrap && window.bootstrap.Modal) {
                    // Get or create modal instance
                    let modal = window.bootstrap.Modal.getInstance(modalElement);
                    if (!modal) {
                      modal = new window.bootstrap.Modal(modalElement, {
                        backdrop: true,  // Click outside to close
                        keyboard: true,  // ESC key to close
                        focus: true
                      });
                    }
                    modal.show();
                  }
                }, 150);
              }}
            />
          ) : (
            // List View
            <>
              {!loading && filteredOrders.length === 0 ? (
                <div className="card bg-white shadow-sm">
                  <div className="card-body text-center py-5">
                    <Package size={64} className="text-muted mb-3" />
                    <h5 className="text-muted">No orders found</h5>
                    <p className="text-muted">
                      {orders.length === 0
                        ? "Orders will appear here when customers place them"
                        : "No orders match your current filters"}
                    </p>
                    {orders.length === 0 && (
                      <button
                        className="btn btn-primary mt-2"
                        onClick={refreshOrders}
                        disabled={loading}
                      >
                        <RefreshCw size={16} className="me-2" />
                        Refresh Orders
                      </button>
                    )}
                  </div>
                </div>
              ) : loading ? null : (
                <div className="row g-4">
              {filteredOrders.map((order) => (
                <div key={order.id} className="col-xl-4 col-lg-6 col-md-6">
                  <div className="card order-card h-100">
                    <div className="card-header d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="mb-0 fw-bold text-primary">
                          {order.id}
                        </h6>
                        <small className="text-muted">
                          {order.orderDate} at {order.orderTime}
                        </small>
                      </div>
                      {getStatusBadge(order.status)}
                    </div>

                    <div className="card-body">
                      {/* Customer Information */}
                      <div className="mb-3">
                        <h6 className="mb-1 d-flex align-items-center">
                          <User size={16} className="me-2 text-secondary" />
                          {order.customerName}
                        </h6>
                        <p className="mb-1 text-muted small">
                          📞 {order.customerPhone}
                        </p>
                        {order.customerEmail !== "N/A" && (
                          <p className="mb-1 text-muted small">
                            ✉️ {order.customerEmail}
                          </p>
                        )}
                        <div className="d-flex align-items-start gap-2">
                          <p className="mb-0 text-muted small flex-grow-1">
                            <MapPin size={14} className="me-1" />
                            {order.customerAddress}
                            {order.deliveryLocation?.city && (
                              <span className="ms-1">
                                • {order.deliveryLocation.city}{order.deliveryLocation.pincode ? ` - ${order.deliveryLocation.pincode}` : ''}
                              </span>
                            )}
                          </p>
                          {order.deliveryLocation?.latitude && order.deliveryLocation?.longitude && (
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => openGoogleMaps(
                                order.deliveryLocation.latitude,
                                order.deliveryLocation.longitude,
                                order.customerAddress
                              )}
                              title="View on Google Maps"
                              style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                            >
                              <Navigation size={12} className="me-1" />
                              Map
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Order Items Preview */}
                      <div className="mb-3">
                        <h6 className="mb-2 small fw-bold">Order Items:</h6>
                        <div className="small">
                          {(expandedOrders.has(order.id)
                            ? order.items
                            : order.items.slice(0, 2)
                          ).map((item, index) => (
                            <div
                              key={index}
                              className="d-flex justify-content-between align-items-center mb-2 p-2 bg-light rounded"
                            >
                              <div className="d-flex align-items-center">
                                {item.image && (
                                  <img
                                    src={item.image}
                                    alt={item.name}
                                    className="me-2"
                                    style={{
                                      width: "30px",
                                      height: "30px",
                                      objectFit: "cover",
                                      borderRadius: "4px",
                                    }}
                                  />
                                )}
                                <div>
                                  <div className="fw-semibold">{item.name}</div>
                                  <div
                                    className="text-muted"
                                    style={{ fontSize: "0.75rem" }}
                                  >
                                    {item.brandName} • Qty: {item.quantity}
                                  </div>
                                </div>
                              </div>
                              <span className="fw-bold">
                                ₹
                                {(
                                  (item.quantity || 0) * (item.price || 0)
                                ).toFixed(2)}
                              </span>
                            </div>
                          ))}
                          {order.items.length > 2 && (
                            <button
                              className="btn btn-link btn-sm p-0 text-decoration-none text-primary"
                              onClick={() => toggleOrderExpansion(order.id)}
                            >
                              {expandedOrders.has(order.id)
                                ? `Show less`
                                : `+${order.items.length - 2} more items`}
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Payment & Delivery */}
                      <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="small text-muted">Payment:</span>
                          <span className="small fw-bold">
                            {order.paymentMethod}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <span className="small text-muted">Delivery:</span>
                          {order.deliveryDate ? (
                            <div className="d-flex align-items-center">
                              <Calendar size={14} className="me-1 text-info" />
                              <span className="small">
                                {order.deliveryDate}
                              </span>
                            </div>
                          ) : (
                            <span className="text-muted small">Not set</span>
                          )}
                        </div>
                        {order.assignedAgent && (
                          <div className="d-flex justify-content-between align-items-center">
                            <span className="small text-muted">Agent:</span>
                            <div className="d-flex align-items-center">
                              <Users size={14} className="me-1 text-primary" />
                              <span className="small">
                                {order.assignedAgent.name}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Total Amount */}
                      <div className="border-top pt-2 mb-3">
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="fw-bold">Total Amount:</span>
                          <span className="fw-bold text-primary fs-5">
                            ₹{(order.totalAmount || 0).toFixed(2)}
                          </span>
                        </div>
                      </div>

                      {/* Notes */}
                      {order.notes && (
                        <div className="mb-3">
                          <h6 className="mb-1 small fw-bold">Notes:</h6>
                          <p className="small text-muted mb-0">{order.notes}</p>
                        </div>
                      )}
                    </div>

                    <div className="card-footer bg-white border-top">
                      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                        <div className="d-flex flex-wrap gap-2">
                          <button
                            className="btn btn-outline-secondary btn-sm"
                            data-bs-toggle="modal"
                            data-bs-target={`#orderModal${order.id}`}
                          >
                            View Details
                          </button>
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => openInvoice(order)}
                          >
                            <FileText size={14} className="me-1" />
                            Invoice
                          </button>
                        </div>
                        <div className="d-flex flex-wrap justify-content-end">
                          {getStatusActions(order)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
              )}
            </>
          )}
        </div>

        {/* Order Details Modal */}
        {filteredOrders.map((order) => (
          <div
            key={order.id}
            className="modal fade"
            id={`orderModal${order.id}`}
            tabIndex="-1"
            aria-labelledby={`orderModalLabel${order.id}`}
            aria-hidden="true"
          >
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title" id={`orderModalLabel${order.id}`}>
                    Order Details - {order.id}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-6">
                      <h6>Customer Information</h6>
                      <p>
                        <User size={16} className="me-2" /> {order.customerName}
                      </p>
                      <p>
                        <strong>Phone:</strong> {order.customerPhone}
                      </p>
                      {order.customerEmail !== "N/A" && (
                        <p>
                          <strong>Email:</strong> {order.customerEmail}
                        </p>
                      )}
                      <div className="d-flex align-items-start gap-2 mb-2">
                        <div className="flex-grow-1">
                          <p className="mb-1">
                            <MapPin size={16} className="me-2" />{" "}
                            {order.customerAddress}
                          </p>
                          {order.deliveryLocation?.city && (
                            <p className="mb-0 text-muted small ms-4">
                              📍 {order.deliveryLocation.city}
                              {order.deliveryLocation.state && `, ${order.deliveryLocation.state}`}
                              {order.deliveryLocation.pincode && ` - ${order.deliveryLocation.pincode}`}
                            </p>
                          )}
                          {order.deliveryLocation?.latitude && order.deliveryLocation?.longitude && (
                            <p className="mb-0 text-muted small ms-4">
                              🌐 Coordinates: {order.deliveryLocation.latitude.toFixed(6)}, {order.deliveryLocation.longitude.toFixed(6)}
                            </p>
                          )}
                        </div>
                        {order.deliveryLocation?.latitude && order.deliveryLocation?.longitude && (
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => openGoogleMaps(
                              order.deliveryLocation.latitude,
                              order.deliveryLocation.longitude,
                              order.customerAddress
                            )}
                            title="View on Google Maps"
                          >
                            <Map size={14} className="me-1" />
                            View on Map
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <h6>Order Information</h6>
                      <p>
                        <strong>Order Date:</strong> {order.orderDate} at{" "}
                        {order.orderTime}
                      </p>
                      <p>
                        <strong>Payment Method:</strong> {order.paymentMethod}
                      </p>
                      <p>
                        <strong>Status:</strong> {getStatusBadge(order.status)}
                      </p>
                      {order.deliveryDate && (
                        <p>
                          <Calendar size={16} className="me-2" /> Delivery Date:{" "}
                          {order.deliveryDate}
                        </p>
                      )}
                      {order.assignedAgent && (
                        <p>
                          <Users size={16} className="me-2" /> Assigned Agent:{" "}
                          {order.assignedAgent.name} (
                          {order.assignedAgent.bikeNumber})
                        </p>
                      )}
                    </div>
                  </div>

                  <hr />

                  <h6>Order Items</h6>
                  <div className="table-responsive">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>Brand</th>
                          <th>Category</th>
                          <th>Quantity</th>
                          <th>Unit Price</th>
                          <th>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {order.items.map((item) => (
                          <tr key={item.id}>
                            <td>
                              <div className="d-flex align-items-center">
                                {item.image && (
                                  <img
                                    src={item.image}
                                    alt={item.name}
                                    className="me-2"
                                    style={{
                                      width: "40px",
                                      height: "40px",
                                      objectFit: "cover",
                                      borderRadius: "4px",
                                    }}
                                  />
                                )}
                                <div>
                                  <div className="fw-semibold">{item.name}</div>
                                  {item.originalPrice > item.discountPrice && (
                                    <small className="text-muted">
                                      <s>₹{item.originalPrice}</s>{" "}
                                      <span className="text-success">
                                        Save ₹
                                        {item.originalPrice -
                                          item.discountPrice}
                                      </span>
                                    </small>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td>{item.brandName}</td>
                            <td>{item.category}</td>
                            <td>{item.quantity}</td>
                            <td>₹{(item.price || 0).toFixed(2)}</td>
                            <td className="fw-bold">
                              ₹
                              {(
                                (item.quantity || 0) * (item.price || 0)
                              ).toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="border-top pt-3 mt-3">
                    <div className="row">
                      <div className="col-md-6">
                        {order.notes && (
                          <div>
                            <strong>Order Notes:</strong>
                            <p className="text-muted">{order.notes}</p>
                          </div>
                        )}
                      </div>
                      <div className="col-md-6">
                        <div className="text-end">
                          <div className="d-flex justify-content-between mb-1">
                            <span>Subtotal:</span>
                            <span>₹{(order.subTotal || 0).toFixed(2)}</span>
                          </div>
                          <div className="d-flex justify-content-between mb-1">
                            <span>Shipping:</span>
                            <span>
                              ₹{(order.shippingCharge || 0).toFixed(2)}
                            </span>
                          </div>
                          <hr className="my-2" />
                          <div className="d-flex justify-content-between fw-bold fs-5">
                            <span>Total Amount:</span>
                            <span className="text-primary">
                              ₹{(order.totalAmount || 0).toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <div className="d-flex justify-content-between align-items-center w-100">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      data-bs-dismiss="modal"
                    >
                      Close
                    </button>
                    <div className="d-flex flex-wrap">
                      {getStatusActions(order)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Delivery Date Selection Modal */}
      {showDeliveryModal && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <Truck size={20} className="me-2" />
                  Assign Order to Delivery Agent
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowDeliveryModal(false);
                    setDeliveryDate("");
                    setDeliveryTimeSlot("");
                    setDeliveryNotes("");
                    setAssignedAgent("");
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <p>
                  Please select a delivery date, time slot and assign an agent
                  for order <strong>{selectedOrder}</strong>
                </p>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label htmlFor="deliveryDate" className="form-label">
                      <Calendar size={16} className="me-2" />
                      Delivery Date *
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      id="deliveryDate"
                      value={deliveryDate}
                      onChange={(e) => setDeliveryDate(e.target.value)}
                      min={new Date().toISOString().split("T")[0]}
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label htmlFor="deliveryTimeSlot" className="form-label">
                      <Clock size={16} className="me-2" />
                      Time Slot *
                    </label>
                    <select
                      className="form-select"
                      id="deliveryTimeSlot"
                      value={deliveryTimeSlot}
                      onChange={(e) => setDeliveryTimeSlot(e.target.value)}
                      required
                    >
                      <option value="">Select time slot</option>
                      <option value="9AM-12PM">9AM - 12PM (Morning)</option>
                      <option value="12PM-3PM">12PM - 3PM (Afternoon)</option>
                      <option value="3PM-6PM">3PM - 6PM (Evening)</option>
                      <option value="6PM-9PM">6PM - 9PM (Night)</option>
                      <option value="Same Day">Same Day (Urgent)</option>
                      <option value="Next Day">Next Day (Standard)</option>
                    </select>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-12 mb-3">
                    <label htmlFor="assignedAgent" className="form-label">
                      <Users size={16} className="me-2" />
                      Assign Agent *
                    </label>
                    <select
                      className="form-select"
                      id="assignedAgent"
                      value={assignedAgent}
                      onChange={(e) => setAssignedAgent(e.target.value)}
                      required
                    >
                      <option value="">Select an agent</option>
                      {agents.length === 0 && (
                        <option disabled>No agents available</option>
                      )}
                      {agents
                        .filter((agent) => agent.status === "active")
                        .map((agent) => {
                          // Count how many orders this agent currently has assigned
                          const assignedOrdersCount = orders.filter(
                            (order) => order.assignedAgent?.id === agent.id
                          ).length;

                          return { ...agent, assignedOrdersCount };
                        })
                        .sort(
                          (a, b) =>
                            a.assignedOrdersCount - b.assignedOrdersCount
                        ) // Sort by workload (least busy first)
                        .map((agent) => (
                          <option key={agent.id} value={agent.id}>
                            {agent.name} - {agent.bikeNumber} (
                            {agent.assignedOrdersCount} orders)
                          </option>
                        ))}
                    </select>
                    <div className="form-text text-muted">
                      <small>
                        📋 Agents can handle multiple orders simultaneously.
                        Agents are sorted by current workload (least busy
                        first).
                      </small>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-12 mb-3">
                    <label htmlFor="deliveryNotes" className="form-label">
                      <FileText size={16} className="me-2" />
                      Delivery Notes (Optional)
                    </label>
                    <textarea
                      className="form-control"
                      id="deliveryNotes"
                      rows="3"
                      value={deliveryNotes}
                      onChange={(e) => setDeliveryNotes(e.target.value)}
                      placeholder="Special instructions for delivery agent..."
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowDeliveryModal(false);
                    setDeliveryDate("");
                    setDeliveryTimeSlot("");
                    setDeliveryNotes("");
                    setAssignedAgent("");
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleDeliveryDateConfirm}
                >
                  <Calendar size={16} className="me-2" />
                  Assign Order
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Modal */}
      {showInvoiceModal && invoiceOrder && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <FileText size={20} className="me-2" />
                  Invoice - {generateInvoiceNumber(invoiceOrder)}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowInvoiceModal(false);
                    setInvoiceOrder(null);
                  }}
                ></button>
              </div>
              <div className="modal-body p-0">
                <div id="invoice-content" className="p-3">
                  {/* Invoice Header - Compact */}
                  <div className="row mb-3 pb-2 border-bottom">
                    <div className="col-md-6">
                      <h3 className="text-primary fw-bold mb-1">
                        World Of Crackers
                      </h3>
                      <p className="mb-0 small">Phone: +91 9137 22 23 24 | worldofcrackers.in</p>
                    </div>
                    <div className="col-md-6 text-end">
                      <h4 className="text-dark mb-1">INVOICE</h4>
                      <p className="mb-0 small">
                        <strong>Invoice #:</strong> {generateInvoiceNumber(invoiceOrder)}
                      </p>
                      <p className="mb-0 small">
                        <strong>Date:</strong> {invoiceOrder.orderDate}
                      </p>
                    </div>
                  </div>

                  {/* Customer Details - Compact */}
                  <div className="row mb-2">
                    <div className="col-md-12">
                      <div className="border p-2">
                        <p className="mb-0 small">
                          <strong>Customer:</strong> {invoiceOrder.customerName} | 
                          <strong> Phone:</strong> {invoiceOrder.customerPhone} | 
                          <strong> Address:</strong> {invoiceOrder.customerAddress}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Items Table */}
                  <div className="table-responsive mb-2">
                    <table className="table table-bordered mb-0" style={{ borderCollapse: 'collapse' }}>
                      <thead style={{ backgroundColor: '#2c3e50', color: 'white' }}>
                        <tr>
                          <th style={{ padding: '10px', borderBottom: '2px solid #000' }}>Item</th>
                          <th style={{ padding: '10px', borderBottom: '2px solid #000' }}>Brand</th>
                          <th className="text-center" style={{ padding: '10px', borderBottom: '2px solid #000', width: '80px' }}>Qty</th>
                          <th className="text-end" style={{ padding: '10px', borderBottom: '2px solid #000', width: '120px' }}>Cost</th>
                          <th className="text-end" style={{ padding: '10px', borderBottom: '2px solid #000', width: '120px' }}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoiceOrder.items.map((item, index) => (
                          <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa' }}>
                            <td style={{ padding: '8px', borderBottom: '1px solid #dee2e6' }}>{item.name}</td>
                            <td style={{ padding: '8px', borderBottom: '1px solid #dee2e6' }}>{item.brandName}</td>
                            <td className="text-center" style={{ padding: '8px', borderBottom: '1px solid #dee2e6' }}>{item.quantity}</td>
                            <td className="text-end" style={{ padding: '8px', borderBottom: '1px solid #dee2e6' }}>
                              ₹{(item.price || 0).toFixed(2)}
                            </td>
                            <td className="text-end fw-bold" style={{ padding: '8px', borderBottom: '1px solid #dee2e6' }}>
                              ₹{((item.quantity || 0) * (item.price || 0)).toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr style={{ backgroundColor: '#e9ecef', fontWeight: 'bold' }}>
                          <td colSpan="4" className="text-end" style={{ padding: '10px', borderTop: '2px solid #000' }}>
                            Subtotal:
                          </td>
                          <td className="text-end" style={{ padding: '10px', borderTop: '2px solid #000' }}>
                            ₹{(invoiceOrder.subTotal || 0).toFixed(2)}
                          </td>
                        </tr>
                        {invoiceOrder.shippingCharge > 0 && (
                          <tr style={{ backgroundColor: '#e9ecef', fontWeight: 'bold' }}>
                            <td colSpan="4" className="text-end" style={{ padding: '10px' }}>
                              Shipping:
                            </td>
                            <td className="text-end" style={{ padding: '10px' }}>
                              ₹{(invoiceOrder.shippingCharge || 0).toFixed(2)}
                            </td>
                          </tr>
                        )}
                        <tr style={{ backgroundColor: '#2c3e50', color: 'white', fontWeight: 'bold', fontSize: '1.1em' }}>
                          <td colSpan="4" className="text-end" style={{ padding: '12px', borderTop: '2px solid #000' }}>
                            Grand Total:
                          </td>
                          <td className="text-end" style={{ padding: '12px', borderTop: '2px solid #000' }}>
                            ₹{(invoiceOrder.totalAmount || 0).toFixed(2)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  {/* Payment Method - Compact */}
                  {invoiceOrder.paymentMethod && (
                    <div className="row mt-1">
                      <div className="col-12">
                        <p className="mb-0 text-muted small">
                          <strong>Payment:</strong> {invoiceOrder.paymentMethod}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Footer - Compact */}
                  <div className="text-center mt-2 pt-2 border-top">
                    <p className="text-muted mb-0 small">
                      Thank you! | For queries: +91 9137 22 23 24
                    </p>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowInvoiceModal(false);
                    setInvoiceOrder(null);
                  }}
                >
                  Close
                </button>
                <div className="btn-group">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={printInvoice}
                  >
                    <Printer size={16} className="me-2" />
                    Print Invoice
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary dropdown-toggle dropdown-toggle-split"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    <span className="visually-hidden">Toggle Dropdown</span>
                  </button>
                  <ul className="dropdown-menu">
                    <li>
                      <button className="dropdown-item" onClick={printInvoice}>
                        <Printer size={16} className="me-2" />
                        Print in New Window
                      </button>
                    </li>
                    <li>
                      <button className="dropdown-item" onClick={simplePrint}>
                        <Printer size={16} className="me-2" />
                        Simple Print
                      </button>
                    </li>
                    <li>
                      <button className="dropdown-item" onClick={downloadPDF}>
                        <Download size={16} className="me-2" />
                        Download as PDF
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .orders-page {
          min-height: 100vh;
          background-color: var(--gray-50);
        }

        /* Enhanced Header */
        .orders-header {
          background: white;
          border-bottom: 1px solid var(--gray-200);
          box-shadow: var(--shadow-sm);
          position: sticky;
          top: 0;
          z-index: 100;
        }

        .header-content {
          padding: 2rem 1.5rem;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 2rem;
          flex-wrap: wrap;
        }

        .header-title-section {
          flex: 1;
          min-width: 250px;
        }

        .page-title {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin: 0 0 0.5rem 0;
          font-size: 1.75rem;
          font-weight: 700;
          color: var(--gray-900);
        }

        .page-title svg {
          color: var(--primary-600);
        }

        .page-subtitle {
          margin: 0;
          color: var(--gray-600);
          font-size: 1rem;
        }

        /* Header Stats */
        .header-stats {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .stat-card {
          background: var(--gray-50);
          border: 1px solid var(--gray-200);
          border-radius: var(--radius-lg);
          padding: 1rem 1.25rem;
          text-align: center;
          min-width: 100px;
          transition: all var(--transition-fast);
        }

        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: var(--shadow-md);
        }

        .stat-total {
          border-color: var(--primary-200);
          background: var(--primary-50);
        }

        .stat-pending {
          border-color: var(--warning-200);
          background: var(--warning-50);
        }

        .stat-delivered {
          border-color: var(--success-200);
          background: var(--success-50);
        }

        .stat-value {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--gray-900);
          margin-bottom: 0.25rem;
        }

        .stat-label {
          font-size: 0.875rem;
          color: var(--gray-600);
          font-weight: 500;
        }

        /* Content Area */
        .orders-content {
          padding: 1.5rem;
        }

        /* Alert Cards */
        .alert-card {
          display: flex;
          align-items: flex-start;
          gap: 1rem;
          padding: 1rem 1.25rem;
          border-radius: var(--radius-lg);
          margin-bottom: 1.5rem;
          border: 1px solid;
        }

        .alert-warning {
          background: var(--warning-50);
          border-color: var(--warning-200);
          color: var(--warning-700);
        }

        .alert-error {
          background: var(--error-50);
          border-color: var(--error-200);
          color: var(--error-700);
        }

        .alert-card svg {
          flex-shrink: 0;
          margin-top: 0.125rem;
        }

        .alert-card strong {
          display: block;
          margin-bottom: 0.25rem;
          font-weight: 600;
        }

        .alert-card p {
          margin: 0;
          font-size: 0.875rem;
        }

        .btn-dismiss {
          background: none;
          border: none;
          color: inherit;
          padding: 0.25rem;
          border-radius: var(--radius-sm);
          cursor: pointer;
          margin-left: auto;
          flex-shrink: 0;
          transition: background-color var(--transition-fast);
        }

        .btn-dismiss:hover {
          background-color: rgba(0, 0, 0, 0.1);
        }

        /* Loading Section */
        .loading-section {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 4rem 2rem;
          text-align: center;
        }

        .loading-spinner {
          margin-bottom: 1rem;
          color: var(--primary-600);
        }

        .loading-text {
          color: var(--gray-600);
          font-size: 1rem;
          margin: 0;
        }

        /* Mobile Responsive */
        @media (max-width: 768px) {
          .header-content {
            flex-direction: column;
            padding: 1.5rem 1rem;
            gap: 1.5rem;
          }

          .header-title-section {
            min-width: unset;
            text-align: center;
          }

          .page-title {
            font-size: 1.5rem;
            justify-content: center;
          }

          .header-stats {
            justify-content: center;
            width: 100%;
          }

          .stat-card {
            flex: 1;
            min-width: 80px;
          }

          .orders-content {
            padding: 1rem;
          }

          .alert-card {
            padding: 0.875rem 1rem;
          }
        }

        @media (max-width: 575px) {
          .header-content {
            padding: 1rem 0.75rem;
          }

          .page-title {
            font-size: 1.25rem;
          }

          .stat-card {
            padding: 0.75rem 1rem;
          }

          .stat-value {
            font-size: 1.25rem;
          }

          .orders-content {
            padding: 0.75rem;
          }
        }
      `}</style>
    </div>
  );
};

export default Orders;
